import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';

import { findLegacyStyledNodes, reportLegacyStyledNodes } from './legacy-styles.ts';

/**
 * Pass-through wrapper that warns, in dev, when its children put MUI/Emotion/tss styled markup
 * on the screen. Once the shell is split into a MUI branch and a MUI-free one, anything wrapped
 * here can move over.
 *
 * Two blind spots: MUI portals (dialogs, popovers, tooltips) mount outside this subtree, and MUI
 * APIs without a DOM footprint (`useTheme`, `useMediaQuery`) leave nothing to find.
 */
const MuiDetector = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const root = ref.current;
    if (!import.meta.env.DEV || !root) return;

    let frame = 0;
    const scan = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        reportLegacyStyledNodes(findLegacyStyledNodes(root), pathname),
      );
    };

    scan();

    // Lazy chunks, suspended data and client-only branches land after the first paint.
    const observer = new MutationObserver(scan);
    observer.observe(root, { subtree: true, childList: true, attributeFilter: ['class'] });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [pathname]);

  return (
    // `contents` keeps the wrapper out of layout, so pages lay out exactly as they do today.
    <div ref={ref} className="contents">
      {children}
    </div>
  );
};

export default function MuiFreeLayout() {
  return (
    <MuiDetector>
      <Outlet />
    </MuiDetector>
  );
}
